import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useRef, useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, useDevice, useOpenUnsavedChangesModal } from 'seagull';
import { routes } from '~/constants';
import { useEventReporter, useOverlayContext, useStore, useStoreActions } from '~/contexts';
import { useContactsMap } from '~/hooks/reactQuery/derived/useContactsMap';
import { Intent } from '~/infra/access-control';
import { handleRequest, notEmpty } from '~/infra/utils';
import { proposalTemplateModel } from '~/models';
import { fromDateStampObject, toDateStampObject } from '~/models/time';
import { ClientPreview } from '../../ClientPreview/ClientPreview';
import { RelationshipEffectiveDate } from '../../RelationshipEffectiveDate/RelationshipEffectiveDate';
import { AgreementName } from '../AgreementName/AgreementName';
import AgreementStudioModal, { SubmitType } from '../AgreementStudioModal/AgreementStudioModal';
import { fromAgreementStudioCommon } from '../AgreementStudioPage.utils';
import { TemplateName } from './TemplateName/TemplateName';
const saveTemplateLoader = 'saveTemplate';
export function AgreementStudioTemplateMode(props) {
    const { template } = props;
    const { isMobileDevice } = useDevice();
    const history = useHistory();
    const { api, logBI } = useStoreActions();
    const overlayContext = useOverlayContext();
    const openUnsavedChangesModal = useOpenUnsavedChangesModal();
    const legalTermsDocuments = useStore((state)=>state.legalTerms.data);
    const { reportButtonClick } = useEventReporter();
    const isEditingTemplate = template != null;
    const scrollToTopRef = useRef(null);
    const effectiveDateValue = useMemo(()=>(template === null || template === void 0 ? void 0 : template.effectiveDate) ? fromDateStampObject(template.effectiveDate) : null, [
        template
    ]);
    const [effectiveDate, setEffectiveDate] = useState(effectiveDateValue);
    const [templateName, setTemplateName] = useState(template === null || template === void 0 ? void 0 : template.title);
    const [agreementName, setAgreementName] = useState((template === null || template === void 0 ? void 0 : template.agreementName) || null);
    const [templateNameInvalid, setTemplateNameInvalid] = useState(false);
    const [agreementNameInvalid, setAgreementNameInvalid] = useState(false);
    const [showTemplateNameInvalid, setShowTemplateNameInvalid] = useState(false);
    const [showAgreementNameInvalid, setShowAgreementNameInvalid] = useState(false);
    const { contactsMap, sampleContact } = useContactsMap(props.worksAt.id);
    function goToProposalTemplates() {
        history.push(routes.proposalTemplates);
    }
    const onSubmit = async (data)=>{
        logBI(isEditingTemplate ? 10493 : 10492);
        if (templateName == null || templateName === '' || templateNameInvalid) {
            setShowTemplateNameInvalid(true);
            if (scrollToTopRef.current != null) scrollToTopRef.current();
            return;
        }
        if (agreementNameInvalid) {
            setShowAgreementNameInvalid(true);
            if (scrollToTopRef.current != null) scrollToTopRef.current();
            return;
        }
        const templateToSave = fromAgreementStudioCommon({
            data,
            title: templateName,
            agreementName: (agreementName === null || agreementName === void 0 ? void 0 : agreementName.trim()) || null,
            effectiveDate: effectiveDate ? toDateStampObject(effectiveDate) : null
        });
        overlayContext.startLoader(saveTemplateLoader);
        const result = await handleRequest(isEditingTemplate ? api.proposalTemplates.save(template.id, templateToSave) : api.proposalTemplates.create(templateToSave).then(()=>{}));
        if (!result.success) {
            const { text } = result.error.createRelationshipErrorText();
            overlayContext.showErrorToast(text);
            overlayContext.stopLoader(saveTemplateLoader);
            return;
        }
        goToProposalTemplates();
        overlayContext.showSuccessToast('Proposal template successfully saved', 'proposal-template-saved-toast');
        overlayContext.stopLoader(saveTemplateLoader);
    };
    const templateToCommonWithId = (template)=>{
        var _ref;
        var _template_legalTermsDocuments;
        return {
            id: template.id,
            coverLetter: template.coverLetter,
            extraClause: template.extraClause,
            requirePaymentMethod: template.requirePaymentMethod,
            allowManualPayments: template.allowManualPayments,
            isExistingCustomer: template.isExistingCustomer,
            customNotificationText: template.customNotificationText,
            services: template.services,
            serviceBundles: template.serviceBundles,
            netTerms: template.netTerms,
            legalTermsDocuments: (_ref = (_template_legalTermsDocuments = template.legalTermsDocuments) === null || _template_legalTermsDocuments === void 0 ? void 0 : _template_legalTermsDocuments.filter(notEmpty)) !== null && _ref !== void 0 ? _ref : [],
            coverCreditCardFees: template.feeControls.coverCreditCardFees,
            /** Will automatic ongoing services be billed at the start or end of the billing-period. */ amendmentsApprovalMethod: template.amendmentsApprovalMethod,
            invoicesApprovalMethod: template.invoicesApprovalMethod,
            requestAccountantAccess: template.requestAccountantAccess,
            introVideo: template.introVideo,
            expirationDate: template.expirationDate
        };
    };
    const openPreviewModal = (data, resolvedTitle)=>{
        const draft = proposalTemplateModel.toPreviewData(proposalTemplateModel.fromDTO(fromAgreementStudioCommon({
            data,
            title: templateName || '',
            agreementName: resolvedTitle !== null && resolvedTitle !== void 0 ? resolvedTitle : (agreementName === null || agreementName === void 0 ? void 0 : agreementName.trim()) || null,
            effectiveDate: effectiveDate ? toDateStampObject(effectiveDate) : null
        })));
        reportButtonClick('agreement-previewed-agreement');
        overlayContext.showModal({
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            content: (modalProps)=>/*#__PURE__*/ _jsx(ClientPreview, _object_spread({
                    type: "draft",
                    draftInfo: {
                        draft,
                        sampleContact,
                        contactsMap
                    }
                }, modalProps))
        });
    };
    const ui = isEditingTemplate ? {
        modalProps: {
            draft: templateToCommonWithId(template),
            modalTitle: 'Edit proposal template'
        },
        buttonProps: {
            children: 'Save changes'
        }
    } : {
        modalProps: {
            draft: null,
            modalTitle: 'Create proposal template'
        },
        buttonProps: {
            children: 'Save template',
            intent: Intent.CreateProposalTemplate
        }
    };
    return /*#__PURE__*/ _jsx(AgreementStudioModal, _object_spread_props(_object_spread({}, props, ui.modalProps), {
        legalTermsSourceId: template === null || template === void 0 ? void 0 : template.id,
        legalTermsDocuments: legalTermsDocuments || [],
        agreementTitle: agreementName || undefined,
        effectiveDate: effectiveDate,
        agreementTitleMP: /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                !isEditingTemplate && /*#__PURE__*/ _jsx(TemplateName, {
                    fullWidth: isMobileDevice,
                    templateTitle: templateName || '',
                    setTemplateTitle: setTemplateName,
                    setTemplateTitleValid: (value)=>setTemplateNameInvalid(!value),
                    showWarning: showTemplateNameInvalid,
                    label: "Template name",
                    testId: "agreement-studio-customer-details-input-display-title"
                }),
                /*#__PURE__*/ _jsx(AgreementName, {
                    fullWidth: isMobileDevice,
                    agreementTitle: agreementName || '',
                    setAgreementTitle: setAgreementName,
                    setAgreementTitleValid: (value)=>setAgreementNameInvalid(!value),
                    showWarning: showAgreementNameInvalid,
                    label: "Agreement name",
                    testId: "agreement-studio-customer-details-input-agreement-name",
                    allowEmpty: true
                }),
                /*#__PURE__*/ _jsx(RelationshipEffectiveDate, {
                    value: effectiveDate,
                    disabled: false,
                    onChange: setEffectiveDate,
                    title: "Agreement effective date",
                    description: "The date when you can start providing the services and bill the client."
                })
            ]
        }),
        doScrollToTopRef: scrollToTopRef,
        onSubmitCb: onSubmit,
        shouldClose: (hasUnsavedChanges)=>hasUnsavedChanges ? openUnsavedChangesModal({
                testId: 'warn-unsaved-changes-modal'
            }) : Promise.resolve(true),
        isTemplateProposal: true,
        onClose: goToProposalTemplates,
        additionalAutoSaveDependencies: [
            templateName,
            agreementName
        ],
        openPreviewModal: openPreviewModal,
        renderSubmitButton: (param)=>{
            let { testId, className, onSubmit } = param;
            return /*#__PURE__*/ _jsx(Button, _object_spread({
                testId: testId,
                className: className,
                size: isMobileDevice ? 'small' : 'medium',
                onClick: ()=>onSubmit(SubmitType.SendProposal)
            }, ui.buttonProps));
        }
    }));
}
